import React from "react";
import {
  Modal,
  ScrollView,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import { Text } from "../../Text";
import CircularButton from "../EditItems/CircularButton";
import { comboModalStyles as styles } from "./styles";
import { Product } from "@/app/types/product";
import { SelectedComboChoices } from "@/app/types/combo-type";
import useComboModal from "@/app/hooks/useComboModal";

type ComboModalProps = {
  visible: boolean;
  product: Product;
  quantity: number;
  setQuantity: (quantity: number) => void;
  onClose: () => void;
  onAddToOrder: (
    product: Product,
    quantity: number,
    comboSelections: Record<number, SelectedComboChoices[]>,
  ) => void;
  onRemoveItem?: () => void;
  initialSelections?: Record<number, Record<number, number>>;
};

const ComboModal = ({
  visible,
  product,
  quantity,
  setQuantity,
  onClose,
  onAddToOrder,
  onRemoveItem,
  initialSelections = {},
}: ComboModalProps): JSX.Element => {
  const {
    sections,
    isValid,
    handleRadioSelect,
    handleStepperChange,
    handleAddToOrder,
    getSelectedCount,
    getSectionSelections,
  } = useComboModal(product, quantity, onAddToOrder, initialSelections);

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={onClose}
      accessibilityViewIsModal
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.backdrop} testID="comboModalBackdrop">
          <TouchableWithoutFeedback>
            <View style={styles.container} testID="comboModalContent">
              <ScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
              >
                <View style={styles.header}>
                  <Text style={styles.title}>{product.name}</Text>
                  {onRemoveItem && (
                    <View style={styles.removeButtonContainer}>
                      <TouchableOpacity
                        style={styles.removeItemButton}
                        onPress={onRemoveItem}
                        accessibilityRole="button"
                        accessibilityLabel="Remove Item"
                        testID="remove-item-button"
                      >
                        <Text style={styles.removeItemButtonText}>
                          Remove Item
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>

                {sections.map((section) => {
                  const sectionSel = getSectionSelections(section.id);
                  const selectedCount = getSelectedCount(sectionSel);
                  const isSingleSelect =
                    section.requiredSelections === 1 &&
                    !section.allowDuplicateSelections;

                  return (
                    <View key={section.id}>
                      <View style={styles.sectionHeader}>
                        <Text style={styles.sectionName}>{section.name}</Text>
                        <Text style={styles.sectionCounter}>
                          {`${selectedCount} out of ${section.requiredSelections} Selections`}
                        </Text>
                      </View>
                      <View style={styles.sectionDivider} />

                      {section.products.map((item) => {
                        const qty = sectionSel[item.id] ?? 0;
                        const isSelected = qty > 0;

                        return (
                          <View key={item.id} style={styles.itemRow}>
                            {isSingleSelect ? (
                              <TouchableOpacity
                                onPress={() =>
                                  handleRadioSelect(section, item.id)
                                }
                                accessibilityRole="radio"
                                accessibilityState={{ checked: isSelected }}
                                accessibilityLabel={item.name}
                              >
                                <View
                                  style={
                                    isSelected
                                      ? styles.radioOuterSelected
                                      : styles.radioOuter
                                  }
                                >
                                  {isSelected && (
                                    <View style={styles.radioInner} />
                                  )}
                                </View>
                              </TouchableOpacity>
                            ) : (
                              <View style={styles.stepperRow}>
                                <CircularButton
                                  disabled={qty === 0}
                                  onPress={() =>
                                    handleStepperChange(section, item.id, -1)
                                  }
                                />
                                <Text style={styles.stepperQty}>{qty}</Text>
                                <CircularButton
                                  add
                                  disabled={
                                    selectedCount >= section.requiredSelections
                                  }
                                  onPress={() =>
                                    handleStepperChange(section, item.id, 1)
                                  }
                                />
                              </View>
                            )}
                            <Text style={styles.itemName}>{item.name}</Text>
                          </View>
                        );
                      })}
                    </View>
                  );
                })}
              </ScrollView>

              <View style={styles.footer}>
                <CircularButton
                  disabled={quantity === 1}
                  onPress={() => setQuantity(quantity - 1)}
                />
                <Text style={styles.stepperQty}>{quantity}</Text>
                <CircularButton
                  add
                  disabled={false}
                  onPress={() => setQuantity(quantity + 1)}
                />

                <TouchableOpacity
                  style={[
                    styles.addToOrderButton,
                    !isValid && { opacity: 0.4 },
                  ]}
                  onPress={handleAddToOrder}
                  disabled={!isValid}
                  accessibilityRole="button"
                  accessibilityLabel="Add to order"
                  accessibilityState={{ disabled: !isValid }}
                >
                  <Text style={styles.addToOrderButtonText}>Add to order</Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default ComboModal;
